import React, { useState, useEffect } from 'react';
import { FaBan } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getMediaUrl } from '@/constants/media';
import { formatHandle } from '@/utils/formatName';
import { SettingsPanel, SettingsRow } from './primitives';
import './BlockedUsersSettings.scss';

interface BlockedUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
}

export const BlockedUsersSettings: React.FC = () => {
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ blockedUsers: BlockedUser[] }>('/users/blocked')
      .then(({ data }) => setBlockedUsers(data.blockedUsers))
      .catch(() => toast.error('Failed to load blocked users'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleUnblock = async (user: BlockedUser) => {
    setUnblocking(user.id);
    try {
      await api.delete(`/users/${user.id}/block`);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success(`${user.display_name} has been unblocked`);
    } catch {
      toast.error('Failed to unblock user');
    } finally {
      setUnblocking(null);
    }
  };

  return (
    <SettingsPanel
      title="Blocked Users"
      description="Users you've blocked cannot message you or view your profile."
    >
      {isLoading ? (
        <SettingsRow title="Loading…" />
      ) : blockedUsers.length === 0 ? (
        <SettingsRow
          icon={<FaBan />}
          title="No blocked users"
          description="You haven't blocked anyone."
        />
      ) : (
        blockedUsers.map((user) => (
          <SettingsRow
            key={user.id}
            icon={
              user.avatar_url ? (
                <img
                  className="blocked-user-avatar"
                  src={getMediaUrl(user.avatar_url) || undefined}
                  alt={user.display_name}
                />
              ) : (
                <span className="blocked-user-avatar blocked-user-avatar--placeholder">
                  {user.display_name[0]?.toUpperCase()}
                </span>
              )
            }
            title={user.display_name}
            description={formatHandle(user.username)}
            control={
              <button
                type="button"
                className="blocked-user-unblock"
                onClick={() => handleUnblock(user)}
                disabled={unblocking === user.id}
              >
                {unblocking === user.id ? 'Unblocking…' : 'Unblock'}
              </button>
            }
          />
        ))
      )}
    </SettingsPanel>
  );
};

export default BlockedUsersSettings;
